import type { ReactNode } from 'react';

type Tone = 'info' | 'success' | 'warning' | 'danger';

interface Props {
  tone?: Tone;
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** Omit tone border/background — use as a neutral content frame. */
  plain?: boolean;
}

const TONE: Record<Tone, { cls: string; icon: string; iconCls: string }> = {
  info: {
    cls: 'border-ink-200 bg-ink-50/60 dark:border-ink-700 dark:bg-ink-800/40',
    icon: 'i',
    iconCls: 'bg-ink-200 text-ink-600 dark:bg-ink-700 dark:text-ink-300',
  },
  success: {
    cls: 'border-pos-500/30 bg-pos-50/60 dark:border-pos-500/30 dark:bg-pos-700/10',
    icon: '✓',
    iconCls: 'bg-pos-500 text-white',
  },
  warning: {
    cls: 'border-amber-500/30 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-700/10',
    icon: '!',
    iconCls: 'bg-amber-500 text-white',
  },
  danger: {
    cls: 'border-neg-500/30 bg-neg-50/60 dark:border-neg-500/40 dark:bg-neg-700/10',
    icon: '$!',
    iconCls: 'bg-neg-500 text-white',
  },
};

export function Banner({ tone = 'info', icon, title, children, actions, plain }: Props) {
  const t = TONE[tone];
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border px-3 py-2 text-xs ${
        plain ? 'border-ink-200 bg-white dark:border-ink-700 dark:bg-ink-800' : t.cls
      }`}
      role={tone === 'danger' || tone === 'warning' ? 'alert' : undefined}
    >
      <span
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${t.iconCls}`}
        aria-hidden
      >
        {icon ?? t.icon}
      </span>
      <div className="flex-1 space-y-0.5">
        {title && (
          <div className="font-semibold text-ink-900 dark:text-ink-50">{title}</div>
        )}
        {children && <div className="text-ink-500 dark:text-ink-300">{children}</div>}
      </div>
      {actions && <div className="shrink-0 self-center">{actions}</div>}
    </div>
  );
}
